import { type FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldAlert, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { IdentityApiError, useSignUp } from "@/api/hooks";
import { resolveSafeReturnUrl } from "@/lib/returnUrl";

const subdomainPattern = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;
// Full FQDN — at least one dot, TLD ≥ 2 letters.
const mailDomainPattern = /^(?=.{4,253}$)([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;

export function SignUpPage() {
    const [params] = useSearchParams();
    const [form, setForm] = useState({
        tenantName: "",
        subdomain: "",
        mailDomain: "",
        email: "",
        displayName: "",
        password: ""
    });
    const [error, setError] = useState<string | null>(null);
    const signUp = useSignUp();
    const navigate = useNavigate();

    const update = (key: keyof typeof form) => (e: { target: { value: string } }) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const validate = (): string | null => {
        if (!subdomainPattern.test(form.subdomain.toLowerCase())) {
            return "Tenant handle may contain lowercase letters, digits and hyphens, 3–64 chars, not starting or ending with a hyphen.";
        }
        if (!mailDomainPattern.test(form.mailDomain.toLowerCase())) {
            return "Mail domain must be a full hostname, e.g. acme.com or mail.acme.co.in.";
        }
        if (form.password.length < 12) {
            return "Password must be at least 12 characters.";
        }
        if (!/[A-Z]/.test(form.password) || !/[0-9]/.test(form.password) || !/[^A-Za-z0-9]/.test(form.password)) {
            return "Password must include an uppercase letter, a digit and a non-alphanumeric character.";
        }
        return null;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const localError = validate();
        if (localError) {
            setError(localError);
            return;
        }

        setError(null);
        try {
            await signUp.mutateAsync({
                ...form,
                subdomain: form.subdomain.toLowerCase(),
                mailDomain: form.mailDomain.trim().toLowerCase()
            });
            const raw = params.get("returnUrl");
            if (raw) {
                window.location.assign(resolveSafeReturnUrl(raw));
            } else {
                navigate("/", { replace: true });
            }
        } catch (err) {
            if (err instanceof IdentityApiError) {
                setError(err.detail ? `${err.message} — ${err.detail}` : err.message);
            } else if (err instanceof TypeError) {
                setError(
                    "Couldn't reach the identity service. Make sure it's running at " +
                    (import.meta.env.VITE_IDENTITY_SERVICE_URL ?? "") +
                    " and that your browser accepts the cross-origin request."
                );
            } else {
                setError("Sign-up failed.");
            }
        }
    };

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle>Create a tenant</CardTitle>
                <CardDescription>
                    Provision your organisation and an administrator in one step.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {error ? (
                    <Alert variant="destructive" className="mb-4">
                        <ShieldAlert className="h-4 w-4" />
                        <AlertTitle>Couldn't create your tenant</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="tenantName">Organisation name</Label>
                            <Input
                                id="tenantName"
                                required
                                value={form.tenantName}
                                onChange={update("tenantName")}
                                autoFocus
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="subdomain">Tenant handle</Label>
                            <Input
                                id="subdomain"
                                required
                                placeholder="acme"
                                value={form.subdomain}
                                onChange={update("subdomain")}
                                autoComplete="off"
                            />
                            <p className="text-xs text-muted-foreground">
                                Lowercase letters, digits, hyphens. No dots — this is a handle, not a full domain.
                                {form.subdomain
                                    ? ` Your workspace: ${form.subdomain.toLowerCase()}.athavita.com`
                                    : " e.g. acme → acme.athavita.com"}
                            </p>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="mailDomain">Mail domain</Label>
                        <Input
                            id="mailDomain"
                            required
                            placeholder="acme.com"
                            value={form.mailDomain}
                            onChange={update("mailDomain")}
                            autoComplete="off"
                        />
                        <p className="text-xs text-muted-foreground">
                            The FQDN your users send & receive mail from, e.g. acme.com,
                            mail.acme.co.in, or webmail.athavita.com. Dots allowed. You'll
                            add MX 10 mx1.athavita.com / 20 mx2.athavita.com to this domain
                            in DNS after sign-up.
                        </p>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="displayName">Your name</Label>
                        <Input
                            id="displayName"
                            required
                            value={form.displayName}
                            onChange={update("displayName")}
                            autoComplete="name"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="email">Work email</Label>
                        <Input
                            id="email"
                            type="email"
                            required
                            value={form.email}
                            onChange={update("email")}
                            autoComplete="email"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="password">Password</Label>
                        <Input
                            id="password"
                            type="password"
                            required
                            value={form.password}
                            onChange={update("password")}
                            autoComplete="new-password"
                        />
                        <p className="text-xs text-muted-foreground">
                            At least 12 characters, with one uppercase letter, one digit, and one symbol.
                        </p>
                    </div>
                    <Button type="submit" className="w-full" disabled={signUp.isPending}>
                        <UserPlus className="h-4 w-4" />
                        {signUp.isPending ? "Creating tenant…" : "Create tenant"}
                    </Button>
                </form>
            </CardContent>
            <CardFooter className="text-sm text-muted-foreground">
                Already have an account?&nbsp;
                <Link
                    to={{
                        pathname: "/login",
                        search: params.get("returnUrl")
                            ? `?returnUrl=${encodeURIComponent(params.get("returnUrl")!)}`
                            : ""
                    }}
                    className="font-semibold text-primary hover:underline"
                >
                    Sign in
                </Link>
            </CardFooter>
        </Card>
    );
}
